import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { services } from '../mockData';
import { Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import './RequestForm.css';
import { getApiUrl } from '../utils/api';
import { trackServiceRequest, trackFormSubmission } from '../utils/analytics';

const RequestForm = () => {
  const [formData, setFormData] = useState({
    customerName: '',
    phone: '',
    email: '',
    service: '',
    address: '',
    description: '',
    preferredDate: '',
    preferredTime: '',
    btuCapacity: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Dynamic API URL for production and development compatibility
    const API = getApiUrl();
    
    try {
      console.log('Submitting to:', `${API}/service-requests`);
      const response = await fetch(`${API}/service-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      console.log('Response status:', response.status);
      
      if (response.ok) {
        toast.success('Talebiniz alındı! En kısa sürede sizi arayacağız.');
        
        // Track successful form submission
        trackServiceRequest(formData.service, formData.address);
        trackFormSubmission('service_request_form', {
          service: formData.service,
          has_email: !!formData.email
        });
        
        // Reset form
        setFormData({
          customerName: '',
          phone: '',
          email: '',
          service: '',
          address: '',
          description: '',
          preferredDate: '',
          preferredTime: '',
          btuCapacity: ''
        });
      } else {
        const errorText = await response.text();
        console.error('Server error:', response.status, errorText);
        toast.error('Talep gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
      }
    } catch (error) {
      console.error('Network error:', error);
      toast.error('Bağlantı hatası. Lütfen internet bağlantınızı kontrol edin ve tekrar deneyin.');
    }
  };

  return (
    <div className="request-form-page">
      <Helmet>
        <title>Online Talep Formu | Hızlı Servis Randevusu | Kuşadası Aydın | teknikservisburda.com</title>
        <meta name="description" content="Online talep formu ile hemen servis randevusu alın! Kuşadası ve Aydın'da aynı gün servis garantisi. Buzdolabı, çamaşır makinesi, klima tamiri için ücretsiz ön görüşme." />
        <meta name="keywords" content="online servis talebi, teknik servis randevu formu, beyaz eşya tamiri randevu, acil servis kuşadası, teknik servis çağrı, klima montaj talebi, kombi tamiri randevu" />
        <link rel="canonical" href="https://teknikservisburda.com/talep" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Online Talep Formu | Hızlı Servis Randevusu" />
        <meta property="og:description" content="Online talep formu ile anında servis talebi oluşturun. Aynı gün servis garantisi!" />
        <meta property="og:url" content="https://teknikservisburda.com/talep" />
        <meta property="og:type" content="website" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Online Talep Formu | teknikservisburda.com" />
        <meta name="twitter:description" content="Online talep formu ile hemen servis randevusu alın!" />
      </Helmet>
      
      <Header />
      
      <section className="page-hero">
        <div className="container">
          <h1>Online Talep Formu</h1>
          <p>Formu doldurun, size en kısa sürede dönelim</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="form-layout">
            <div className="form-container">
              <form onSubmit={handleSubmit} className="request-form">
                <div className="form-group">
                  <label htmlFor="customerName">Ad Soyad *</label>
                  <input
                    type="text"
                    id="customerName"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleChange}
                    required
                    placeholder="Adınız ve soyadınız"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Telefon *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="0532 123 4567"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">E-posta</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="ornek@email.com"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="service">Servis Türü *</label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Servis seçiniz</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.name}>
                        {service.name}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.service === 'Klima Montaj' && (
                  <div className="form-group">
                    <label htmlFor="btuCapacity">Klima BTU Kapasitesi *</label>
                    <select
                      id="btuCapacity"
                      name="btuCapacity"
                      value={formData.btuCapacity}
                      onChange={handleChange}
                      required
                    >
                      <option value="">BTU seçiniz</option>
                      <option value="9000">9000 BTU</option>
                      <option value="12000">12000 BTU</option>
                      <option value="18000">18000 BTU</option>
                      <option value="24000">24000 BTU</option>
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="address">Adres *</label>
                  <textarea
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    rows="2"
                    placeholder="Servis adresiniz"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description">Arıza Açıklaması *</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows="4"
                    placeholder="Cihazınızdaki arızayı detaylı olarak açıklayınız"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="preferredDate">Tercih Edilen Tarih</label>
                    <input
                      type="date"
                      id="preferredDate"
                      name="preferredDate"
                      value={formData.preferredDate}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="preferredTime">Tercih Edilen Saat</label>
                    <select
                      id="preferredTime"
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleChange}
                    >
                      <option value="">Saat seçiniz</option>
                      <option value="09:00-12:00">09:00 - 12:00</option>
                      <option value="12:00-15:00">12:00 - 15:00</option>
                      <option value="15:00-18:00">15:00 - 18:00</option>
                    </select>
                  </div>
                </div>

                <button type="submit" className="btn-primary submit-btn">
                  <Send size={20} />
                  Talebi Gönder
                </button>
              </form>
            </div>

            <div className="form-info">
              <div className="info-card">
                <h3>Hızlı Servis Süreci</h3>
                <div className="info-steps">
                  <div className="info-step">
                    <div className="step-number">1</div>
                    <div>
                      <h4>Formu Doldurun</h4>
                      <p>Talebinizi detaylı olarak belirtin</p>
                    </div>
                  </div>
                  <div className="info-step">
                    <div className="step-number">2</div>
                    <div>
                      <h4>Onay Alın</h4>
                      <p>Sizi arayıp randevu oluşturuyoruz</p>
                    </div>
                  </div>
                  <div className="info-step">
                    <div className="step-number">3</div>
                    <div>
                      <h4>Servis Hizmeti</h4>
                      <p>Teknisyenimiz adresinize geliyor</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="info-card">
                <h3>Garantilerimiz</h3>
                <ul className="info-list">
                  <li>
                    <CheckCircle size={20} />
                    <span>Aynı gün servis hizmeti</span>
                  </li>
                  <li>
                    <CheckCircle size={20} />
                    <span>Uzman teknisyen kadrosu</span>
                  </li>
                  <li>
                    <CheckCircle size={20} />
                    <span>Garanti belgeli işçilik</span>
                  </li>
                  <li>
                    <CheckCircle size={20} />
                    <span>Orijinal yedek parça</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RequestForm;